<script lang="ts" setup>
import CheckActive from '../icons/CheckActive.vue';
import CheckCircle from '../icons/CheckCircle.vue';
import AppTypography from './AppTypography.vue';

const { password = '' } = defineProps<{
  password?: string;
}>();

const config = useRuntimeConfig();
const criteria = computed(() => config.public.settings);

const hasEightCharacters = computed(() => password.length >= Number(criteria.value.passwordCriteria.minLength));
const hasOneUppercase = computed(() => {
  const uppercaseMatches = password.match(/[A-Z]/g);
  return uppercaseMatches && uppercaseMatches.length >= Number(criteria.value.passwordCriteria.minUppercase);
});
const hasSpecialCharacter = computed(() => {
  const specialCharMatches = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g);
  return (
    specialCharMatches && specialCharMatches.length >= Number(criteria.value.passwordCriteria.minSpecialCharacters)
  );
});
const hasNumber = computed(() => /\d/.test(password));
</script>
<template>
  <v-sheet
    v-if="password"
    class="password-criteria d-flex flex-column gap-sm"
    :class="{
      'password-criteria--eight-chars': hasEightCharacters,
      'password-criteria--one-uppercase': hasOneUppercase,
      'password-criteria--one-number': hasNumber,
    }"
  >
    <AppTypography class="font-caption-bold text-tertiary text-uppercase">Password must contain:</AppTypography>
    <div class="d-flex flex-column gap-xs">
      <div
        class="d-flex align-center gap-sm"
        :class="hasEightCharacters ? 'text-success' : 'text-secondary'"
        data-testid="eigth-characters"
      >
        <component :is="hasEightCharacters ? CheckActive : CheckCircle" width="24px" height="24px" />
        <AppTypography class="font-footnote-regular">
          at least {{ criteria.passwordCriteria.minLength }} characters
        </AppTypography>
      </div>
      <div
        class="d-flex align-center gap-sm"
        :class="hasOneUppercase ? 'text-success' : 'text-secondary'"
        data-testid="one-uppercase"
      >
        <component :is="hasOneUppercase ? CheckActive : CheckCircle" width="24px" height="24px" />
        <AppTypography class="font-footnote-regular"> Must include both uppercase and lowercase letters </AppTypography>
      </div>
      <div
        class="d-flex align-center gap-sm"
        :class="hasNumber ? 'text-success' : 'text-secondary'"
        data-testid="one-number"
      >
        <component :is="hasNumber ? CheckActive : CheckCircle" width="24px" height="24px" />
        <AppTypography class="font-footnote-regular"> Must contain at least one numeric character (0-9) </AppTypography>
      </div>
      <div
        class="d-flex align-center gap-sm"
        :class="hasSpecialCharacter ? 'text-success' : 'text-secondary'"
        data-testid="one-special-character"
      >
        <component :is="hasSpecialCharacter ? CheckActive : CheckCircle" width="24px" height="24px" />
        <AppTypography class="font-footnote-regular">
          Must contain at least one special character (e.g., !@#$%^&*)
        </AppTypography>
      </div>
    </div>
  </v-sheet>
</template>
